import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  Icon,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaHeartbeat, FaPhone, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { Reveal, RevealGroup, RevealItem } from '../../components/motion';
import { PageHero, SectionHeading, IconMark, InkBand, LightSheet } from '../../components/premium';
import { clearGlass } from '../../components/glass';

const HAIRLINE = 'rgba(14,47,58,0.06)';
const MONO = "'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace";

const Cardiovascular = () => {
  const cptCodes = [
    {
      code: '93656',
      description: 'Atrial fibrillation ablation',
    },
    {
      code: '93653',
      description: 'SVT ablation',
    },
    {
      code: '33208',
      description: 'Dual-chamber pacemaker insertion',
    },
    {
      code: '93458',
      description: 'Left heart catheterization with coronary angiography',
    },
    {
      code: '93306',
      description: 'Complete transthoracic echocardiogram',
    },
  ];

  const features = [
    'Advanced cardiac imaging',
    'Minimally invasive procedures',
    'Electrophysiology expertise',
    'Comprehensive cardiac care',
    'State-of-the-art monitoring',
    'Rapid recovery protocols'
  ];

  return (
    <Box>
      {/* Hero Section */}
      {/* The same photographic hero as every other inner page: the operating
          room under the deep ink scrim, in place of a per-specialty color
          gradient with the photo faded to near invisibility. */}
      <PageHero
        title={"Interventional Cardiology"}
        subtitle={"Advanced cardiac care with precision diagnostics and innovative treatments"}
        image="/facility/operating-room.jpg"
        imagePosition="center"
      />

      {/* Introduction Section */}
      <Container maxW="container.xl" py={{ base: 20, md: 28 }}>
        <VStack spacing={{ base: 14, md: 16 }} align="stretch">
          <Reveal textAlign="center" maxW="4xl" mx="auto">
            <IconMark icon={FaHeartbeat} size="lg" mx="auto" mb={6} />
            <Heading
              size="xl"
              mb={6}
              color="ink.900"
              fontWeight="600"
              letterSpacing="-0.022em"
              lineHeight="1.15"
            >
              Comprehensive Cardiac Care
            </Heading>
            <Text fontSize="lg" color="ink.700" lineHeight="1.75">
              Our interventional cardiology program delivers cutting-edge cardiac care with a focus on patient outcomes
              and safety. From diagnostic procedures to complex interventions, our experienced team provides
              comprehensive interventional cardiology services using the latest technology and evidence-based protocols
              to ensure optimal heart health.
            </Text>
          </Reveal>

          {/* Features Grid */}
          <RevealGroup>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={{ base: 3, md: 4 }}>
              {features.map((feature, index) => (
                <RevealItem key={index}>
                  <HStack
                    h="100%"
                    px={5}
                    py={4}
                    bg="white"
                    borderRadius="18px"
                    boxShadow="cardSoft"
                    border="1px solid"
                    borderColor={HAIRLINE}
                    spacing={4}
                    align="center"
                  >
                    <Icon as={FaCheckCircle} color="brand.600" boxSize={5} flexShrink={0} />
                    <Text color="ink.800" fontWeight="500">{feature}</Text>
                  </HStack>
                </RevealItem>
              ))}
            </Grid>
          </RevealGroup>
        </VStack>
      </Container>

      <LightSheet tone="mist">
        <Container maxW="container.xl">
          {/* CPT Codes Section */}
          <SectionHeading title="Common Procedures & CPT Codes" size="lg" mb={8} />
          <Reveal maxW="4xl" mx="auto">
            <TableContainer
              bg="white"
              borderRadius="24px"
              boxShadow="card"
              border="1px solid"
              borderColor={HAIRLINE}
            >
              <Table
                variant="simple"
                sx={{
                  'th, td': { borderColor: HAIRLINE },
                  'tbody tr:last-of-type td': { borderBottom: 'none' },
                }}
              >
                <Thead bg="mist.50">
                  <Tr>
                    <Th color="ink.600" fontSize="xs" letterSpacing="0.14em" fontWeight="600" py={4}>CPT Code</Th>
                    <Th color="ink.600" fontSize="xs" letterSpacing="0.14em" fontWeight="600" py={4}>Procedure Description</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {cptCodes.map((item, index) => (
                    <Tr key={index} _hover={{ bg: 'mist.50' }} transition="background 0.2s">
                      <Td
                        fontWeight="600"
                        color="ink.900"
                        fontSize="md"
                        fontFamily={MONO}
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {item.code}
                      </Td>
                      <Td color="ink.700">{item.description}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Reveal>

          {/* Why Choose Us Section */}
          <SectionHeading
            title="Why Choose Lake Lucien for Interventional Cardiology?"
            size="lg"
            mt={{ base: 20, md: 24 }}
            mb={8}
          />
          <RevealGroup>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={{ base: 4, md: 6 }}>
              <RevealItem>
                <Box
                  h="100%"
                  bg="white"
                  p={{ base: 6, md: 8 }}
                  borderRadius="24px"
                  boxShadow="card"
                  border="1px solid"
                  borderColor={HAIRLINE}
                >
                  <Heading size="md" mb={4} color="ink.900" fontWeight="600" letterSpacing="-0.01em">Expert Cardiologists</Heading>
                  <Text color="ink.700" lineHeight="1.75">
                    Our board-certified cardiac specialists are leaders in interventional cardiology and
                    electrophysiology, providing expert care for even the most complex cardiac conditions.
                  </Text>
                </Box>
              </RevealItem>
              <RevealItem>
                <Box
                  h="100%"
                  bg="white"
                  p={{ base: 6, md: 8 }}
                  borderRadius="24px"
                  boxShadow="card"
                  border="1px solid"
                  borderColor={HAIRLINE}
                >
                  <Heading size="md" mb={4} color="ink.900" fontWeight="600" letterSpacing="-0.01em">Advanced Diagnostics</Heading>
                  <Text color="ink.700" lineHeight="1.75">
                    State-of-the-art imaging and diagnostic capabilities enable precise evaluation and
                    treatment planning for optimal outcomes in cardiac procedures.
                  </Text>
                </Box>
              </RevealItem>
              <RevealItem>
                <Box
                  h="100%"
                  bg="white"
                  p={{ base: 6, md: 8 }}
                  borderRadius="24px"
                  boxShadow="card"
                  border="1px solid"
                  borderColor={HAIRLINE}
                >
                  <Heading size="md" mb={4} color="ink.900" fontWeight="600" letterSpacing="-0.01em">Minimally Invasive Options</Heading>
                  <Text color="ink.700" lineHeight="1.75">
                    We prioritize minimally invasive techniques whenever possible, resulting in shorter
                    recovery times, less pain, and better cosmetic outcomes for our patients.
                  </Text>
                </Box>
              </RevealItem>
              <RevealItem>
                <Box
                  h="100%"
                  bg="white"
                  p={{ base: 6, md: 8 }}
                  borderRadius="24px"
                  boxShadow="card"
                  border="1px solid"
                  borderColor={HAIRLINE}
                >
                  <Heading size="md" mb={4} color="ink.900" fontWeight="600" letterSpacing="-0.01em">Comprehensive Care</Heading>
                  <Text color="ink.700" lineHeight="1.75">
                    From diagnosis through treatment and follow-up, we provide complete interventional cardiology
                    care with a multidisciplinary approach focused on your heart health.
                  </Text>
                </Box>
              </RevealItem>
            </Grid>
          </RevealGroup>
        </Container>
      </LightSheet>

      {/* CTA Section */}
      <InkBand image="/facility/exterior.jpg">
        <Container maxW="container.xl">
          <VStack spacing={0} textAlign="center">
            <SectionHeading
              tone="dark"
              title="Your Heart Health is Our Priority"
              lede="Schedule a consultation with our interventional cardiology specialists today"
              mb={10}
            />
            <HStack spacing={4} flexWrap="wrap" justify="center" rowGap={3}>
              <Button
                as={RouterLink}
                to="/contact"
                leftIcon={<FaPhone />}
                size="lg"
                px={8}
                variant="brandOnDark"
              >
                Contact Us
              </Button>
              <Button
                as={RouterLink}
                to="/specialties"
                leftIcon={<FaArrowLeft />}
                size="lg"
                px={8}
                color="white"
                fontWeight="600"
                sx={clearGlass}
                transition="transform 0.35s cubic-bezier(0.16,1,0.3,1), background-color 0.2s"
                _hover={{ bg: 'rgba(255,255,255,0.16)', transform: 'translateY(-2px)' }}
                _active={{ bg: 'rgba(255,255,255,0.2)' }}
              >
                All Specialties
              </Button>
            </HStack>

            {/* Contact Info Box */}
            <Reveal w="100%" maxW="2xl" mt={{ base: 14, md: 16 }}>
              <Box p={{ base: 6, md: 8 }} borderRadius="24px" textAlign="center" sx={clearGlass}>
                <VStack spacing={3}>
                  <Text fontWeight="600" color="white" fontSize="lg">
                    Need Immediate Cardiac Care Information?
                  </Text>
                  <Text color="whiteAlpha.800">
                    Our cardiac care coordinators are ready to assist you
                  </Text>
                  <HStack spacing={4} justify="center">
                    <Text color="whiteAlpha.900" fontSize="lg">
                      Call us at{' '}
                      <Text as="span" fontWeight="700" color="white">
                        (321) 321-3935
                      </Text>
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </Reveal>
          </VStack>
        </Container>
      </InkBand>
    </Box>
  );
};

export default Cardiovascular;
